import t from 'react-translate';
import React, { useContext } from 'react';
import { css } from '@emotion/core';
import * as yup from 'yup';
import { useFormContext } from 'react-hook-form';

// redux
import { NQuizQuestion } from 'redux/schemas/models/quiz';
import { useSelector, useDispatch } from 'react-redux';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { editAnswerFeedback } from 'redux/actions/quizzes';
import { wrapThunkAction } from 'redux/utils';

import QuestionSetContext, { QuestionContext, SavingIndicator } from 'lecture_pages/services/question-set-context';
import NvFroala from 'froala/components/nv-froala';
import { halfSpacing, quarterSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { danger, gray2, gray3, gray4, gray5, gray6, gray7, primary, success } from 'styles/global_defaults/colors';
import ViewAnswerFeedBack from 'quizzes/components/feedback-components/view-answer-feedback';
import { textLargeBodyFontSize, textLargeLineHeight } from 'styles/global_defaults/fonts';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import NvIcon from 'shared/components/nv-icon';
import ViewValidationError from 'quizzes/components/view-validation-error';
import NvPopover from 'shared/components/nv-popover';
import ValidationErrorMessage from 'shared/components/validation-error-message';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import NvTextArea from 'shared/components/inputs/nv-text-area';

type LongAnswerSectionProps = {
  currentQuestion: NQuizQuestion;
};

const LongAnswerSection = (props: LongAnswerSectionProps) => {
  const {
    savingIndicatorTimeoutRef,
    setSavingStatus,
    currentQuestionResponse,
    isHardDeadlineExpired,
    isContentManagementCollection,
  } = useContext(QuestionSetContext);
  const { setValue, getValues, trigger, formState } = useFormContext() || {};
  const { currentQuestion } = props;

  const {
    answerState,
    setAnswerState,
    setAnswerInputError,
    answerInputError,
    responseOptions,
  } = React.useContext(QuestionContext);

  const {
    isEditMode,
    isAnswerMode,
    isCurrentActivityType,
    getAnswerInputBackgroundColor,
    showAnswerFeedback,
    dataQaQuestionSetAttributes,
    getQuestionLabelElementId,
  } = useQuestionSetHelpers();

  const getBorderColor = () => {
    if (answerInputError) {
      return danger;
    }
    if (currentQuestionResponse) {
      if (isCurrentActivityType(ComponentType.SURVEY)) {
        return gray3;
      }
      if (!currentQuestionResponse.response) {
        return gray5;
      }
      if (currentQuestionResponse?.isCorrect) {
        return success;
      }
      return danger;
    }
    return gray3;
  };

  const styles = css`
    .disabled-text-area {
      min-height: 140px !important;
      border: 1px solid ${gray4};
      border-radius: ${quarterSpacing}px;
      background-color: ${gray6};
      ::placeholder {
        color: ${gray3};
      }
    }
    .froala-answer-input {
      .fr-wrapper {
        border: 1px solid ${getBorderColor()} !important;
        font-size: ${textLargeBodyFontSize}px !important;
        border-radius: ${quarterSpacing}px !important;
        .fr-element {
          border-radius: ${quarterSpacing}px !important;
          background-color: ${getAnswerInputBackgroundColor()} !important;
          ${currentQuestion.isRequired && css`
            &::after {
              content: '*';
              color: ${gray2};
              position: absolute;
              right: ${halfSpacing}px;
              top: ${halfSpacing}px;
            }
          `}
        }
        .fr-element:focus {
          outline: none !important;
        }
        .fr-placeholder {
          border: none !important;
          color: ${gray2};
          border-radius: ${quarterSpacing}px !important;
        }
        .fr-disabled {
          cursor: default !important;
        }
        .fr-disabled ~ .fr-placeholder {
          background-color: ${gray6} !important;
          color: ${gray3} !important;
          border-radius: ${quarterSpacing}px !important;
        }
      }
      .fr-wrapper:focus-within {
        border-color: ${(answerInputError || formState?.errors[currentQuestion.id.toString()]) ? danger : primary} !important;
        background-color: ${gray7} !important;
      }
    }

    .feedback-container {
      .fr-placeholder {
        font-size: ${textLargeBodyFontSize}px !important;
        background-color: white !important;
        border: none !important;
        line-height: ${textLargeLineHeight}px !important;
      }
      .fr-wrapper:not(.show-placeholder) .fr-element {
        background-color: white !important;
      }
      .fr-element {
        border: 1px solid ${gray3};
        border-radius: ${quarterSpacing}px;
        font-size: ${textLargeBodyFontSize}px !important;
        padding: ${threeQuartersSpacing}px !important;
        line-height: ${textLargeLineHeight}px !important;
        p {
          margin-bottom: 0px;
        }
      }
      .fr-element:focus {
        outline: none !important;
        border: 1px solid ${primary};
      }
    }
  `;
  const defaultFeedbackTemplate = '<p class="froala-style-medium"><br /></p>';

  const dispatch = useDispatch();

  const courseAliases = useSelector(getFlatCourseAliases);

  const { explanation } = responseOptions[0];

  const answerInputValidationSchema = yup.string()
    .required(t.VALIDATION.REQUIRED())
    .max(16000, t.VALIDATION.CHARACTER_LIMIT_POPOVER_ERROR('16,000'));

  const response: string = currentQuestionResponse?.response?.toString();
  const [feedback, setFeedback] = React.useState(explanation ?? defaultFeedbackTemplate);

  const handleFeedbackChange = (html) => {
    setFeedback(html);
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);
    wrapThunkAction(dispatch(editAnswerFeedback(
      {
        explanation: html,
        secondaryExplanation: '',
        questionOptionId: responseOptions[0].id,
      },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const handleAnswerChange = (html) => {
    const textContent = html
      .replace(/<[^>]*>?/gm, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with a normal space
      .trim();

    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      setAnswerState(html);
      answerInputValidationSchema.validate(textContent)
        .then(() => {
          setAnswerInputError(null);
        })
        .catch((err) => {
          setAnswerInputError(err);
        });
    } else {
      setValue(currentQuestion.id.toString(), html);
      trigger(currentQuestion.id.toString());
    }
  };

  const getValue = () => {
    if (currentQuestionResponse) {
      return response;
    }
    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      if (typeof answerState !== 'string') {
        return '';
      }
      return answerState;
    }
    return getValues(currentQuestion.id.toString());
  };

  const showMaxCharLimitError = () => {
    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      return answerInputError?.type === 'max';
    }
    return formState?.errors[currentQuestion.id.toString()]?.type === 'max';
  };

  const isDisabled = !isAnswerMode
   || !!currentQuestionResponse
   || isHardDeadlineExpired
   || isContentManagementCollection;

  const getEditModeContent = () => {
    if (isCurrentActivityType(ComponentType.SURVEY)) {
      // This disabled text area is to show how the long answer question will look like in the learner view for surveys
      return (
        <NvTextArea
          placeholder={t.FORM.PLACEHOLDER.INPUT_YOUR_ANSWER()}
          disabled
          textareaClassName='disabled-text-area bg-transparent text-large-body'
        />
      );
    }
    return (
      <>
        <div className='d-flex mb-2'>
          <p className='semi-bold mb-0'>{t.QUIZZES.LONG_ANSWER_QUESTION.FEEDBACK_FOR_ANY_ANSWER()}: </p>
          <NvPopover
            showOnHover
            className='d-flex align-items-center'
            placement='top'
            content={(
              <div className=''>
                {t.QUIZZES.LONG_ANSWER_QUESTION.SETTING_LONG_ANSWER_INFO(courseAliases)}
              </div>
              )}
          >
            <NvIcon className='d-flex' icon='info ml-1' size='xs-smallest text-primary' />
          </NvPopover>
        </div>
        <div>
          <NvFroala
            allowToolbar
            value={feedback}
            className='feedback-container'
            minHeight={60}
            onChange={handleFeedbackChange}
            placeholder={t.QUIZZES.LONG_ANSWER_QUESTION.FEEDBACK_PLACEHOLDER()}
            dataQa={dataQaQuestionSetAttributes.questionModal.longAnswerQuestion.addFeedback}
          />
        </div>
      </>
    );
  };

  return (
    <div css={styles}>
      {!isEditMode && (
        <div className='mb-5'>
          <NvPopover
            placement='top'
            show={isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)
              ? !!answerInputError
              : !!formState?.errors[currentQuestion.id.toString()]}
            preventOverflow
            content={(
              <ValidationErrorMessage
                headerClassName='semi-bold'
                header={t.VALIDATION.ERROR()}
                text={isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)
                  ? answerInputError?.message
                  : formState?.errors[currentQuestion.id.toString()]?.message}
              />
          )}
          >
            <NvFroala
              value={getValue()}
              minHeight={120}
              className='froala-answer-input'
              onChange={handleAnswerChange}
              placeholder={t.FORM.PLACEHOLDER.INPUT_YOUR_ANSWER()}
              isDisabled={isDisabled}
              required={currentQuestion.isRequired}
              ariaLabelledBy={getQuestionLabelElementId(currentQuestion.id)}
            />
          </NvPopover>
          {showMaxCharLimitError() && (
            <ViewValidationError characterLimit='16,000' />
          )}
          {currentQuestionResponse?.isCorrect && showAnswerFeedback && (
            <ViewAnswerFeedBack currentQuestion={currentQuestion} />
          )}
        </div>
      )}
      {isEditMode && (
        getEditModeContent()
      )}
    </div>
  );
};

export default LongAnswerSection;
